import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { AdminBookingDetailDto, getAdminBooking, useApiClient } from '../api/client';
import { BookingTimeline, bookingPersonName, formatBookingTime } from '../components/BookingTimeline';
import { RideOperationsTabs } from '../components/RideOperationsTabs';
import { PageHeader, PageShell, StatusBadge } from '../components/ui';
import { useI18n } from '../i18n/I18nProvider';

export function BookingDetailPanel({ bookingId, refreshKey = 0 }: { bookingId: string; refreshKey?: number }) {
  const { request } = useApiClient(); const { t, language } = useI18n();
  const [detail, setDetail] = React.useState<AdminBookingDetailDto | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [lastSuccess, setLastSuccess] = React.useState<string | null>(null);
  const [retry, setRetry] = React.useState(0); const [loading, setLoading] = React.useState(false);
  const generation = React.useRef(0);
  React.useEffect(() => {
    const ticket = ++generation.current; let disposed = false;
    setLoading(true); setError(null);
    getAdminBooking(request, bookingId).then(data => {
      if (disposed || ticket !== generation.current) return;
      setDetail(data); setLastSuccess(new Date().toISOString());
    }).catch(err => { if (!disposed && ticket === generation.current) setError((err as Error)?.message || t('bookings.loadFailed')); })
      .finally(() => { if (!disposed && ticket === generation.current) setLoading(false); });
    return () => { disposed = true; };
  }, [bookingId, refreshKey, request, retry, t]);
  // Never display a previous booking's details while navigating to a different ID.
  const current = detail?.booking.bookingId === bookingId ? detail : null;
  return <div className="rounded-lg border border-gray-200 bg-white p-5">
    {error && <div role="alert" className="mb-4 text-sm text-red-700">{error} <button className="btn btn-neutral ml-2" onClick={() => setRetry(n => n + 1)}>{t('bookings.retry')}</button></div>}
    {loading && !current && <p>{t('common.loading')}</p>}
    {current && <>
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <StatusBadge>{t(`bookings.phase.${current.booking.phase}`)}</StatusBadge>
        <div className="flex gap-4 text-sm">
          <Link className="text-gray-900 hover:underline" to={`/rides/${encodeURIComponent(current.booking.rideId)}`}>{t('bookings.ride')}</Link>
          <Link className="text-gray-900 hover:underline" to={`/payment-ops?bookingId=${encodeURIComponent(bookingId)}`}>Payment Ops</Link>
        </div>
      </div>
      <dl className="mb-6 grid gap-4 text-sm sm:grid-cols-3">
        <div><dt className="text-gray-500">{t('bookings.requester')}</dt><dd>{current.booking.requester ? <Link className="text-gray-900" to={`/users/${encodeURIComponent(current.booking.requester.id)}`}>{bookingPersonName(current.booking.requester)}</Link> : '—'}</dd></div>
        <div><dt className="text-gray-500">{t('bookings.driver')}</dt><dd>{current.booking.driver ? <Link className="text-gray-900" to={`/users/${encodeURIComponent(current.booking.driver.id)}`}>{bookingPersonName(current.booking.driver)}</Link> : '—'}</dd></div>
        <div><dt className="text-gray-500">{t('bookings.scheduled')}</dt><dd>{formatBookingTime(current.booking.rideTime, language)}</dd></div>
        <div><dt className="text-gray-500">{t('bookings.route')}</dt><dd>{current.booking.pickup?.publicName || '—'} → {current.booking.dropoff?.publicName || '—'}</dd></div>
      </dl>
      <BookingTimeline detail={current} />
    </>}
    {current && lastSuccess && <p className="mt-4 text-xs text-gray-500">{t('bookings.lastSuccess')} <time dateTime={lastSuccess}>{formatBookingTime(lastSuccess, language)}</time> · Europe/Vienna</p>}
  </div>;
}
export const BookingDetailsPage: React.FC = () => {
  const { bookingId = '' } = useParams(); const { t } = useI18n(); const [refreshKey, setRefreshKey] = React.useState(0);
  React.useEffect(() => {
    const refresh = () => { if (document.visibilityState === 'visible') setRefreshKey(n => n + 1); };
    const interval = window.setInterval(refresh, 30000);
    document.addEventListener('visibilitychange', refresh);
    return () => { window.clearInterval(interval); document.removeEventListener('visibilitychange', refresh); };
  }, []);
  return <PageShell><RideOperationsTabs /><PageHeader title={t('bookings.detailTitle')}
    description={bookingId} actions={<button className="btn btn-neutral" onClick={() => setRefreshKey(n => n + 1)}>{t('action.refresh')}</button>} />
    <BookingDetailPanel key={bookingId} bookingId={bookingId} refreshKey={refreshKey} />
  </PageShell>;
};
